import etuBg from './etu.webp';
import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../Firebase';
import { Search, Filter, Edit2, Eye, CheckCircle, XCircle, ChevronDown } from 'lucide-react';

const NIVEAUX = ['A1','A2','B1','B2','C1','C2'];

export default function AdminEtudiants({ staff }) {
  const canEdit = ['it_manager','general_manager','communication'].includes(staff?.role);
  const [etudiants, setEtudiants] = useState([]);
  const [filtered, setFiltered]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filterNiveau, setFilterNiveau] = useState('');
  const [filterPays, setFilterPays]     = useState('');
  const [selected, setSelected]   = useState(null);
  const [editing, setEditing]     = useState(false);
  const [editData, setEditData]   = useState({});
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState('');

  useEffect(() => { fetchEtudiants(); }, []);

  async function fetchEtudiants() {
    setLoading(true);
    const snap = await getDocs(collection(db, 'etudiants'));
    const data = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(e => e.role === 'etudiant' || !e.role);
    setEtudiants(data);
    setFiltered(data);
    setLoading(false);
  }

  useEffect(() => {
    let res = [...etudiants];
    if (search)       res = res.filter(e => e.nom?.toLowerCase().includes(search.toLowerCase()) || e.email?.toLowerCase().includes(search.toLowerCase()));
    if (filterNiveau) res = res.filter(e => e.niveau === filterNiveau);
    if (filterPays)   res = res.filter(e => e.pays === filterPays);
    setFiltered(res);
  }, [search, filterNiveau, filterPays, etudiants]);

  const pays = [...new Set(etudiants.map(e => e.pays).filter(Boolean))];

  async function handleSave() {
    setSaving(true);
    await updateDoc(doc(db, 'etudiants', selected.id), { ...editData, updatedAt: serverTimestamp() });
    setMsg('Modifications enregistrées.');
    await fetchEtudiants();
    setEditing(false);
    setSaving(false);
    setTimeout(() => setMsg(''), 3000);
  }

  async function toggleActif(etudiant) {
    await updateDoc(doc(db, 'etudiants', etudiant.id), { actif: !etudiant.actif });
    await fetchEtudiants();
  }

  if (loading) return <div className="admin-page-loading"><div className="admin-spinner"/></div>;

  // Vue détail étudiant
  if (selected && !editing) return (
    <div className="admin-page admin-page-bg--etudiants"><div style={{backgroundImage:`url(${etuBg})`,backgroundSize:"cover",backgroundPosition:"center",position:"fixed",inset:0,opacity:0.15,pointerEvents:"none",zIndex:0}}/>
      <div className="admin-page__header">
        <button className="admin-back-btn" onClick={() => setSelected(null)}>← Retour</button>
        <div className="admin-page__actions">
          {canEdit && <>
          <button className="admin-btn admin-btn--secondary" onClick={() => { setEditing(true); setEditData({ nom: selected.nom, email: selected.email, pays: selected.pays, niveau: selected.niveau, presentiel: selected.presentiel, actif: selected.actif }); }}>
            <Edit2 size={15}/> Modifier
          </button>
          <button className={`admin-btn ${selected.actif ? 'admin-btn--danger' : 'admin-btn--success'}`} onClick={() => toggleActif(selected)}>
            {selected.actif ? <><XCircle size={15}/> Désactiver</> : <><CheckCircle size={15}/> Activer</>}
          </button>
          </>}
        </div>
      </div>

      <div className="admin-detail-grid">
        <div className="admin-detail-card">
          <div className="admin-detail-avatar">{selected.nom?.charAt(0).toUpperCase()}</div>
          <h2>{selected.nom}</h2>
          <p>{selected.email}</p>
          <div className="admin-detail-badges">
            <span className="admin-badge admin-badge--niveau">{selected.niveau || 'A1'}</span>
            <span className={`admin-badge ${selected.actif ? 'admin-badge--ok' : 'admin-badge--err'}`}>{selected.actif ? 'Actif' : 'Inactif'}</span>
            {selected.presentiel && <span className="admin-badge admin-badge--info">Présentiel</span>}
          </div>
        </div>
        <div className="admin-detail-info">
          <h3>Informations</h3>
          <div className="admin-info-grid">
            <div><label>Pays</label><span>{selected.pays || '—'}</span></div>
            <div><label>Date de naissance</label><span>{selected.dateNaissance || '—'}</span></div>
            <div><label>Niveau</label><span>{selected.niveau || 'A1'}</span></div>
            <div><label>Mode</label><span>{selected.presentiel ? 'Présentiel' : 'En ligne'}</span></div>
            <div><label>Inscription</label><span>{selected.createdAt ? new Date(selected.createdAt.seconds * 1000).toLocaleDateString('fr-FR') : '—'}</span></div>
          </div>
        </div>
      </div>
    </div>
  );

  // Vue édition
  if (selected && editing) return (
    <div className="admin-page admin-page-bg--etudiants"><div style={{backgroundImage:`url(${etuBg})`,backgroundSize:"cover",backgroundPosition:"center",position:"fixed",inset:0,opacity:0.15,pointerEvents:"none",zIndex:0}}/>
      <div className="admin-page__header">
        <button className="admin-back-btn" onClick={() => setEditing(false)}>← Annuler</button>
        <button className="admin-btn admin-btn--primary" onClick={handleSave} disabled={saving}>
          {saving ? <span className="admin-spinner-sm"/> : 'Enregistrer'}
        </button>
      </div>
      {msg && <div className="admin-msg admin-msg--ok">{msg}</div>}
      <div className="admin-form-grid">
        {[['nom','Nom complet','text'],['email','Email','email'],['pays','Pays','text'],['niveau','Niveau','select'],['dateNaissance','Date de naissance','date']].map(([field,label,type]) => (
          <div key={field} className="admin-form-field">
            <label>{label}</label>
            {type === 'select' ? (
              <select value={editData[field] || ''} onChange={e => setEditData(p => ({...p, [field]: e.target.value}))}>
                {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            ) : (
              <input type={type} value={editData[field] || ''} onChange={e => setEditData(p => ({...p, [field]: e.target.value}))} />
            )}
          </div>
        ))}
        <div className="admin-form-field">
          <label>Mode</label>
          <select value={editData.presentiel ? 'presentiel' : 'enligne'} onChange={e => setEditData(p => ({...p, presentiel: e.target.value === 'presentiel'}))}>
            <option value="enligne">En ligne</option>
            <option value="presentiel">Présentiel</option>
          </select>
        </div>
      </div>
    </div>
  );

  // Vue liste
  return (
    <div className="admin-page admin-page-bg--etudiants"><div style={{backgroundImage:`url(${etuBg})`,backgroundSize:"cover",backgroundPosition:"center",position:"fixed",inset:0,opacity:0.15,pointerEvents:"none",zIndex:0}}/>
      <div className="admin-page__header">
        <div>
          <h2>Étudiants</h2>
          <p>{filtered.length} étudiant{filtered.length > 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="admin-filters">
        <div className="admin-search">
          <Search size={16}/>
          <input type="text" placeholder="Rechercher par nom ou email..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select value={filterNiveau} onChange={e => setFilterNiveau(e.target.value)} className="admin-select">
          <option value="">Tous les niveaux</option>
          {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <select value={filterPays} onChange={e => setFilterPays(e.target.value)} className="admin-select">
          <option value="">Tous les pays</option>
          {pays.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      <div className="admin-table-box">
        <table className="admin-table">
          <thead>
            <tr><th>Nom</th><th>Email</th><th>Pays</th><th>Niveau</th><th>Mode</th><th>Statut</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={7} style={{textAlign:'center',padding:'2rem',color:'var(--admin-muted)'}}>Aucun étudiant trouvé</td></tr>}
            {filtered.map(e => (
              <tr key={e.id}>
                <td><strong>{e.nom}</strong></td>
                <td>{e.email}</td>
                <td>{e.pays || '—'}</td>
                <td><span className="admin-badge admin-badge--niveau">{e.niveau || 'A1'}</span></td>
                <td>{e.presentiel ? 'Présentiel' : 'En ligne'}</td>
                <td><span className={`admin-badge ${e.actif ? 'admin-badge--ok' : 'admin-badge--err'}`}>{e.actif ? 'Actif' : 'Inactif'}</span></td>
                <td>
                  <div className="admin-table-actions">
                    <button className="admin-icon-btn" onClick={() => setSelected(e)} title="Voir"><Eye size={15}/></button>
                    {canEdit && <>
                    <button className="admin-icon-btn" onClick={() => { setSelected(e); setEditing(true); setEditData({ nom: e.nom, email: e.email, pays: e.pays, niveau: e.niveau, presentiel: e.presentiel, actif: e.actif }); }} title="Modifier"><Edit2 size={15}/></button>
                    <button className={`admin-icon-btn ${e.actif ? 'admin-icon-btn--danger' : 'admin-icon-btn--success'}`} onClick={() => toggleActif(e)} title={e.actif ? 'Désactiver' : 'Activer'}>
                      {e.actif ? <XCircle size={15}/> : <CheckCircle size={15}/>}
                    </button>
                    </>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}